import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import Ably from 'ably';
import { useChannel } from 'ably/react';
import { useCallback, useRef } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

type OwnershipChangedData = {
  tokenId: string;
  contractAddress: string;
  operation: 'add' | 'remove';
};

interface IProps {
  onChange?: (context: OwnershipChangedData[]) => void;
  userId?: string;
  enabled?: boolean;
  debounce?: boolean;
  debounceMs?: number;
}

/**
 * Custom hook to listen for user ownership changes and trigger a callback.
 * @function useUserOwnershipChanged
 * @param {IProps} props - The properties for the hook.
 * @param {string} [props.userId] - The user ID to listen for ownership changes.
 * @param {(context: OwnershipChangedData[]) => void} [props.onChange] - The callback function to be called when ownership changes.
 * @param {boolean} [props.enabled=true] - Whether the listener is enabled.
 * @param {boolean} [props.debounce=false] - Whether to debounce the callback.
 * @param {number} [props.debounceMs=500] - The debounce delay in milliseconds.
 */
export const useUserOwnershipChanged = ({
  userId,
  onChange,
  debounce,
  debounceMs = 500,
  enabled,
}: IProps) => {
  const { user } = useDynamicContext();

  const queue = useRef<OwnershipChangedData[]>([]);

  const channelName = `user:${userId || user?.userId}`;

  const contextChanged = useCallback(() => {
    const context = [...queue.current];
    queue.current = [];
    if (!onChange) return;
    onChange(context);
  }, [onChange]);

  const debouncedContextChanged = useCallback(
    useDebounceCallback(contextChanged, debounceMs, {
      leading: false,
      trailing: true,
    }),
    [contextChanged],
  );

  const callback = useCallback(
    (message: Ably.Message) => {
      if (enabled === false || message?.data?.type !== 'OwnershipChange')
        return;
      const data: OwnershipChangedData = message.data;
      if (!data.tokenId || !data.operation || !data.contractAddress) return;
      queue.current.push({
        ...data,
        contractAddress: data.contractAddress.toLowerCase(),
      });
      if (debounce) {
        debouncedContextChanged();
        return;
      }
      contextChanged();
    },
    [enabled, debounce, contextChanged],
  );

  useChannel(channelName, callback);
};

export { type OwnershipChangedData };
